import { useEffect, useRef, useState } from 'react'
import config from '../../../config'
import type {
    CreateCRMRequest,
    CRMDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    ProspectoDTO,
    StorageFile,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    errorMessage,
    formatSpokenDate,
    isMoral,
    NOTE_TYPES,
    noteMeta,
    personName,
    phoneHref,
    prospectContact,
    statusKey,
} from '../common/prospect-common'
import type { BitacoraFilter } from '../common/prospect-common'

export type ProspectDrawerTab = 'general' | 'bitacora'

export interface ProspectDrawerProps {
    prospectoId: number | null
    onClose: () => void
    onEdit?: (prospectoId: number) => void
    onDeleted?: (prospecto?: ProspectoDTO | null) => void
}

export type LoadState = 'idle' | 'loading' | 'ready' | 'error'

export interface ProspectDrawerTabItem {
    id: ProspectDrawerTab
    label: string
    count?: number | null
}

const BITACORA_ERROR = 'No se pudo cargar la bitácora.'
const SAVE_NOTE_ERROR = 'No se pudo guardar la bitácora.'
const UPLOAD_ERROR = 'No se pudo subir el archivo.'

export const isBusy = (state: LoadState) => state === 'idle' || state === 'loading'

export const useProspectDrawerModel = ({ prospectoId, onClose, onDeleted }: ProspectDrawerProps) => {
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const bitacoraLoadedFor = useRef<number | null>(null)
    const [prospecto, setProspecto] = useState<ProspectoDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [tab, setTab] = useState<ProspectDrawerTab>('general')
    const [toast, setToast] = useState<string | null>(null)
    const [bitacora, setBitacora] = useState<CRMTextoDTO[]>([])
    const [bitacoraState, setBitacoraState] = useState<LoadState>('idle')
    const [bitacoraError, setBitacoraError] = useState<string | null>(null)
    const [noteType, setNoteType] = useState<CRMTextoTipo>(NOTE_TYPES[0].id)
    const [note, setNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [bitacoraFilter, setBitacoraFilter] = useState<BitacoraFilter>('todo')

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        if (!prospectoId) return undefined
        setProspecto(null)
        setError(null)
        setConfirmDelete(false)
        setTab('general')
        setBitacora([])
        setBitacoraState('idle')
        setBitacoraError(null)
        bitacoraLoadedFor.current = null
        setNote('')
        setNoteType(NOTE_TYPES[0].id)
        setBitacoraFilter('todo')
        return undefined
    }, [prospectoId])

    useEffect(() => {
        if (!prospectoId) return undefined
        let active = true
        const load = async () => {
            const fallback = 'No se pudo cargar el detalle del prospecto.'
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/prospectos/${prospectoId}`)
                const payload: WrapperResponse<ProspectoDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || fallback)
                setProspecto(payload.body)
            } catch (loadError) {
                if (active) {
                    setProspecto(null)
                    setError(errorMessage(loadError, fallback))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [prospectoId, reloadToken])

    useEffect(() => {
        if (!prospectoId || tab !== 'bitacora' || bitacoraLoadedFor.current === prospectoId) return undefined
        let active = true
        const load = async () => {
            try {
                setBitacoraState('loading')
                setBitacoraError(null)
                const response = await APIInvoke.invokeGET(`/crm?prospectoId=${prospectoId}`)
                const payload: WrapperResponse<CRMDTO[]> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || BITACORA_ERROR)
                const textos = payload.body?.[0]?.textos || []
                setBitacora([...textos].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || ''))))
                setBitacoraState('ready')
                bitacoraLoadedFor.current = prospectoId
            } catch (loadError) {
                if (active) {
                    setBitacoraState('error')
                    setBitacoraError(errorMessage(loadError, BITACORA_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [prospectoId, tab])

    const deleteProspecto = async () => {
        if (!prospecto?.id || deleting) return
        const fallback = 'No se pudo eliminar el prospecto.'
        try {
            setConfirmDelete(false)
            setDeleting(true)
            const payload: WrapperResponse<ProspectoDTO> | null = await (await APIInvoke.invokeDELETE(`/prospectos/${prospecto.id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || fallback)
            onDeleted?.(payload.body)
            onClose()
        } catch (deleteError) {
            setError(errorMessage(deleteError, fallback))
        } finally {
            setDeleting(false)
        }
    }

    const prependBitacora = (created?: CRMTextoDTO | null) => {
        if (!created) return
        setBitacora((items) => [created, ...items.filter((item) => item.id !== created.id)])
        setBitacoraState('ready')
        setBitacoraFilter('todo')
        bitacoraLoadedFor.current = prospecto?.id || prospectoId
    }

    const addNote = async () => {
        const texto = note.trim()
        if (!texto || !prospecto || savingNote) return
        try {
            setSavingNote(true)
            const request: CreateCRMRequest = {
                asunto: 'bitacora',
                tipo: noteType,
                prospectoId: prospecto.id,
                textos: [{ tipo: noteType, texto }],
            }
            const response = await APIInvoke.invokePOST('/crm', request)
            const payload: WrapperResponse<CRMDTO> | null = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || SAVE_NOTE_ERROR)
            prependBitacora(payload.body?.textos?.[0])
            setNote('')
            showToast('Registro agregado a la bitácora')
        } catch (saveError) {
            showToast(errorMessage(saveError, SAVE_NOTE_ERROR))
        } finally {
            setSavingNote(false)
        }
    }

    const uploadFile = async (file?: File | null) => {
        if (!file || !prospecto || uploading) return
        try {
            setUploading(true)
            const body = new FormData()
            body.append('bucket', config.bucket.name)
            body.append('path', `prospectos/${file.name}`)
            body.append('file', file)
            const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
            if (!uploaded.ok || !uploaded.body) throw new Error(uploaded.message || UPLOAD_ERROR)
            const request: CreateCRMRequest = {
                asunto: 'bitacora',
                tipo: 'adjunto',
                prospectoId: prospecto.id,
                textos: [{
                    tipo: 'adjunto',
                    texto: '',
                    adjunto: {
                        dominio: '',
                        path: uploaded.body.url,
                        archivo: file.name,
                    },
                }],
            }
            const saved: WrapperResponse<CRMDTO> = await (await APIInvoke.invokePOST('/crm', request)).json()
            if (!saved.ok) throw new Error(saved.message || 'No se pudo registrar el archivo.')
            prependBitacora(saved.body?.textos?.[0])
            showToast('Archivo agregado a la bitácora')
        } catch (uploadError) {
            showToast(errorMessage(uploadError, UPLOAD_ERROR))
        } finally {
            setUploading(false)
        }
    }

    const openFile = async (path?: string | null) => {
        try {
            const url: string | null | undefined = await APIInvoke.resolveStoredFileUrl(path)
            if (url) window.open(url, '_blank', 'noopener')
        } catch (fileError) {
            showToast(errorMessage(fileError, 'No se pudo abrir el archivo.'))
        }
    }

    const { email, phone } = prospectContact(prospecto)
    const tabs: ProspectDrawerTabItem[] = [
        { id: 'general', label: 'Datos generales' },
        { id: 'bitacora', label: 'Bitácora', count: bitacoraState === 'ready' ? bitacora.length : null },
    ]

    return {
        prospecto,
        loading,
        error,
        tab,
        tabs,
        toast,
        name: prospecto ? personName(prospecto) : '',
        moral: isMoral(prospecto?.fiscal),
        activo: statusKey(prospecto?.status) === 'activo',
        phone,
        email,
        whatsapp: phoneHref(phone),
        apellidos: [prospecto?.apPat, prospecto?.apMat].filter(Boolean).join(' '),
        registroSpoken: formatSpokenDate(prospecto?.fechaRegistro),
        confirmDelete,
        deleting,
        bitacoraState,
        bitacoraError,
        bitacoraFilter,
        visibleNotes: bitacora.filter((item) => bitacoraFilter === 'todo' || noteMeta(item.tipo).filter === bitacoraFilter),
        noteType,
        note,
        notePlaceholder: NOTE_TYPES.find((item) => item.id === noteType)?.placeholder,
        savingNote,
        uploading,
        setTab,
        setNoteType,
        setNote,
        setBitacoraFilter,
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
        reload: () => setReloadToken((token) => token + 1),
        deleteProspecto,
        addNote,
        uploadFile,
        openFile,
    }
}
